/**
 * 管理后台（仅平台 admin 角色；入口在导航「系统」分组，非 admin 不可见）。
 *
 * 两个 Tab：
 * - 用户管理：按邮箱 / 邀请 ID 搜索 + 角色过滤 + 分页；行内改角色、禁用 / 启用。
 *   禁用是危险操作（连坐撤销该账号的登录会话），二次确认；不允许对自己操作（后端同样拦截）。
 * - 审计日志：audit_logs 表其实一直在写（注册/登录/画像/匹配/成队…），这里补上「有人看」的入口。
 *
 * 所有失败由 App.tsx 的全局 mutationCache/queryCache 统一弹 Toast（不允许静默失败）。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminUserUpdate } from '../api/admin.js';
import type { AdminStatsDto, AdminUserDto } from '../api/types.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.jsx';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { formatDateTime } from '../lib/format.js';
import { userRoleLabel } from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';
import { useMeQuery } from '../hooks/queries.js';
import { useSession } from '../store/session.js';
import type { UserRole } from '../store/session.js';
import { toast } from '../store/toast.js';

type Tab = 'users' | 'audit';
const PAGE_SIZE = 20;
const ROLE_OPTIONS: UserRole[] = ['student', 'organizer', 'admin'];

export function AdminPage() {
  const meQuery = useMeQuery();
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('users');

  // ---- 用户管理筛选 ----
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [page, setPage] = useState(0);

  // ---- 审计日志筛选 ----
  const [actionInput, setActionInput] = useState('');
  const [action, setAction] = useState('');
  const [auditPage, setAuditPage] = useState(0);

  const statsQuery = useQuery({
    queryKey: qk.adminStats(),
    queryFn: () => adminApi.stats(),
  });

  const usersQuery = useQuery({
    queryKey: qk.adminUsers({ keyword, role: role === '' ? undefined : role, page, pageSize: PAGE_SIZE }),
    queryFn: () =>
      adminApi.users({
        keyword: keyword === '' ? undefined : keyword,
        role: role === '' ? undefined : role,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: tab === 'users',
  });

  const auditQuery = useQuery({
    queryKey: qk.adminAudit({ action, page: auditPage, pageSize: PAGE_SIZE }),
    queryFn: () =>
      adminApi.auditLogs({
        action: action === '' ? undefined : action,
        page: auditPage,
        pageSize: PAGE_SIZE,
      }),
    enabled: tab === 'audit',
  });

  /** 改角色 / 禁用启用：成功后失效用户列表与概览（角色分布会变） */
  const updateUser = useMutation({
    mutationFn: ({ id, input }: { id: string; input: AdminUserUpdate }) => adminApi.updateUser(id, input),
    onSuccess: (user) => {
      void queryClient.invalidateQueries({ queryKey: qk.adminUsersRoot() });
      void queryClient.invalidateQueries({ queryKey: qk.adminStats() });
      toast.success(
        `已更新 ${user.name}`,
        user.disabledAt !== null ? '该账号已禁用，其登录状态立即失效' : undefined,
      );
    },
  });

  const [disableTarget, setDisableTarget] = useState<AdminUserDto | null>(null);

  // 非 admin（含角色刚被改掉）：不渲染任何管理数据，只说明原因
  const currentRole = meQuery.data?.user.role ?? me?.role;
  if (currentRole !== undefined && currentRole !== 'admin') {
    return (
      <div className="stack">
        <div className="page-head">
          <div>
            <h1 className="page-title">管理后台</h1>
            <p className="page-sub">该页面仅对平台管理员开放。</p>
          </div>
        </div>
        <Card title="无访问权限">
          <p className="page-sub">
            当前角色是{userRoleLabel(currentRole)}，管理后台需要 admin 角色。如果你是平台管理员但看到这个提示，
            请用管理员账号重新登录。
          </p>
        </Card>
      </div>
    );
  }

  const users = usersQuery.data;
  const audit = auditQuery.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">管理后台</h1>
          <p className="page-sub">用户管理与操作审计。所有操作都会写进审计日志。</p>
        </div>
      </div>

      {statsQuery.data !== undefined ? <StatsCards stats={statsQuery.data} /> : null}

      <div className="row-tight">
        <Button variant={tab === 'users' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('users')}>
          用户管理
        </Button>
        <Button variant={tab === 'audit' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('audit')}>
          审计日志
        </Button>
      </div>

      {tab === 'users' ? (
        <Card
          title="用户管理"
          subtitle="按邮箱或邀请 ID（CM-XXXXXX）搜索；禁用后该账号立即无法登录与使用任何功能。"
        >
          <form
            className="row-tight"
            onSubmit={(event) => {
              event.preventDefault();
              setKeyword(keywordInput.trim());
              setPage(0);
            }}
          >
            <input
              className="input"
              value={keywordInput}
              placeholder="邮箱或 CM-XXXXXX"
              aria-label="搜索用户"
              onChange={(event) => setKeywordInput(event.target.value)}
            />
            <select
              className="select"
              value={role}
              aria-label="按角色过滤"
              onChange={(event) => {
                setRole(event.target.value as UserRole | '');
                setPage(0);
              }}
            >
              <option value="">全部角色</option>
              {ROLE_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {userRoleLabel(item)}
                </option>
              ))}
            </select>
            <Button type="submit" variant="secondary" size="sm">
              搜索
            </Button>
          </form>

          {usersQuery.isPending ? <Loading label="正在加载用户…" /> : null}
          {usersQuery.error !== null && usersQuery.error !== undefined ? (
            <ErrorState error={usersQuery.error} onRetry={() => void usersQuery.refetch()} />
          ) : null}

          {users !== undefined && users.items.length === 0 ? (
            <EmptyState
              title="没有符合条件的用户"
              description={keyword === '' ? '换个角色筛选看看。' : `没有匹配「${keyword}」的用户，检查一下邮箱或邀请 ID。`}
            />
          ) : null}

          {users?.items.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              isSelf={user.id === (me?.id ?? meQuery.data?.user.id)}
              busy={updateUser.isPending && updateUser.variables?.id === user.id}
              onChangeRole={(next) => updateUser.mutate({ id: user.id, input: { role: next } })}
              onDisable={() => setDisableTarget(user)}
              onEnable={() => updateUser.mutate({ id: user.id, input: { disabled: false } })}
            />
          ))}

          {users !== undefined && users.total > 0 ? (
            <div className="row">
              <span className="field-hint">
                第 {users.page + 1} 页 / 共 {users.total} 人
              </span>
              <span className="grow" />
              <Button
                size="sm"
                variant="secondary"
                disabled={users.page === 0}
                onClick={() => setPage((value) => Math.max(0, value - 1))}
              >
                上一页
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={(users.page + 1) * users.pageSize >= users.total}
                onClick={() => setPage((value) => value + 1)}
              >
                下一页
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {tab === 'audit' ? (
        <Card title="审计日志" subtitle="谁在什么时候做了什么（时间倒序）。可按 action 模糊过滤，如 user. / team. / auth.。">
          <form
            className="row-tight"
            onSubmit={(event) => {
              event.preventDefault();
              setAction(actionInput.trim());
              setAuditPage(0);
            }}
          >
            <input
              className="input"
              value={actionInput}
              placeholder="action 前缀，如 user."
              aria-label="按 action 过滤"
              onChange={(event) => setActionInput(event.target.value)}
            />
            <Button type="submit" variant="secondary" size="sm">
              过滤
            </Button>
          </form>

          {auditQuery.isPending ? <Loading label="正在加载审计日志…" /> : null}
          {auditQuery.error !== null && auditQuery.error !== undefined ? (
            <ErrorState error={auditQuery.error} onRetry={() => void auditQuery.refetch()} />
          ) : null}

          {audit !== undefined && audit.items.length === 0 ? (
            <EmptyState title="没有日志" description={action === '' ? '还没有任何操作记录。' : `没有匹配「${action}」的日志。`} />
          ) : null}

          {audit?.items.map((item) => (
            <div key={item.id} className="row-tight">
              <Tag tone="muted">{item.action}</Tag>
              <span className="field-hint">
                {item.userName ?? '系统'} · {formatDateTime(item.createdAt)}
              </span>
              <span className="grow" />
              <code className="field-hint">{JSON.stringify(item.meta)}</code>
            </div>
          ))}

          {audit !== undefined && audit.total > 0 ? (
            <div className="row">
              <span className="field-hint">
                第 {audit.page + 1} 页 / 共 {audit.total} 条
              </span>
              <span className="grow" />
              <Button
                size="sm"
                variant="secondary"
                disabled={audit.page === 0}
                onClick={() => setAuditPage((value) => Math.max(0, value - 1))}
              >
                上一页
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={(audit.page + 1) * audit.pageSize >= audit.total}
                onClick={() => setAuditPage((value) => value + 1)}
              >
                下一页
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      <ConfirmDialog
        open={disableTarget !== null}
        title="禁用该账号？"
        danger
        confirmText="确认禁用"
        pending={updateUser.isPending}
        description={
          <>
            将禁用 <strong>{disableTarget?.name}</strong>（{disableTarget?.email}）。
            禁用后 TA 已登录的会话立即失效，且在启用前无法登录或使用任何功能。此操作会记入审计日志。
          </>
        }
        onConfirm={() => {
          if (disableTarget !== null) updateUser.mutate({ id: disableTarget.id, input: { disabled: true } });
          setDisableTarget(null);
        }}
        onCancel={() => setDisableTarget(null)}
      />
    </div>
  );
}

/** 概览卡：一屏看清平台规模。 */
function StatsCards({ stats }: { stats: AdminStatsDto }) {
  return (
    <div className="stats-cards">
      <div className="stat-card">
        <span className="stat-value">{stats.users.total}</span>
        <span className="stat-label">
          用户总数（学生 {stats.users.byRole.student} · 赛事方 {stats.users.byRole.organizer} · 管理员{' '}
          {stats.users.byRole.admin}）
        </span>
      </div>
      <div className="stat-card">
        <span className="stat-value">{stats.users.disabled}</span>
        <span className="stat-label">已禁用账号（另有 {stats.users.deleted} 个已注销）</span>
      </div>
      <div className="stat-card">
        <span className="stat-value">{stats.teams.total}</span>
        <span className="stat-label">队伍总数</span>
      </div>
      <div className="stat-card">
        <span className="stat-value">{stats.candidates.total}</span>
        <span className="stat-label">候选人池条目</span>
      </div>
      <div className="stat-card">
        <span className="stat-value">{stats.events.total}</span>
        <span className="stat-label">赛事总数</span>
      </div>
    </div>
  );
}

/** 用户行：信息 + 行内角色下拉 + 禁用/启用。自己那一行禁改（后端也会拦）。 */
function UserRow({
  user,
  isSelf,
  busy,
  onChangeRole,
  onDisable,
  onEnable,
}: {
  user: AdminUserDto;
  isSelf: boolean;
  busy: boolean;
  onChangeRole: (role: UserRole) => void;
  onDisable: () => void;
  onEnable: () => void;
}) {
  const disabled = user.disabledAt !== null;
  return (
    <div className="row-tight">
      <div className="grow">
        <div className="row-tight">
          <strong>{user.name}</strong>
          <Tag tone={user.role === 'admin' ? 'primary' : 'default'}>{userRoleLabel(user.role)}</Tag>
          {user.emailVerified ? <Tag tone="ok">已验证</Tag> : <Tag tone="warn">未验证</Tag>}
          {disabled ? <Tag tone="danger">已禁用</Tag> : null}
          {user.deletedAt !== null ? <Tag tone="muted">已注销</Tag> : null}
          {isSelf ? <Tag tone="muted">这是你</Tag> : null}
        </div>
        <div className="field-hint">
          {user.email}
          {user.publicId !== null ? ` · ${user.publicId}` : ''} · 注册于 {formatDateTime(user.createdAt)}
        </div>
      </div>
      <select
        className="select"
        value={user.role}
        disabled={isSelf || user.deletedAt !== null}
        aria-label={`修改 ${user.name} 的角色`}
        onChange={(event) => onChangeRole(event.target.value as UserRole)}
      >
        {ROLE_OPTIONS.map((item) => (
          <option key={item} value={item}>
            {userRoleLabel(item)}
          </option>
        ))}
      </select>
      {user.deletedAt !== null ? null : disabled ? (
        <Button size="sm" variant="secondary" loading={busy} onClick={onEnable}>
          启用
        </Button>
      ) : (
        <Button size="sm" variant="danger" loading={busy} disabled={isSelf} onClick={onDisable}>
          禁用
        </Button>
      )}
    </div>
  );
}
